import { AppointmentDto } from "appointment-experience-client";
import { AxiosRequestConfig } from "axios";
import { WorkflowState } from "common-util-experience-client";
import { createAppointmentApi } from "./AppointmentApiHelper";

/**
 * Appointment workflow transitions.
 *
 * Status ids are per-environment workflow data, so a transition is resolved from the
 * appointment's own `status.nextStates` -- the set the backend says is legal from where
 * it is now. This is the logic ScheduleDetails and PostAppointment each carry their own
 * copy of (`getStatusId`); new callers should use this one.
 */

export const loadAppointment = async (
  appointmentId: string,
  options?: AxiosRequestConfig
): Promise<AppointmentDto> => {
  const response = await createAppointmentApi().appointmentControllerGetAppointmentById(
    appointmentId,
    options
  );
  return response.data;
};

/**
 * The state id for `statusName`, or undefined when the appointment cannot move there
 * from its current state. Already being in that status resolves to its own id, so a
 * repeated transition is a no-op rather than an error.
 */
export const findAppointmentStatusId = (
  appointment: AppointmentDto,
  statusName: string
): string | undefined => {
  const target = statusName.toLowerCase();
  const current = appointment.status;

  if (current?.stateName?.toLowerCase() === target) {
    return current.stateId;
  }

  const nextStates: WorkflowState[] = current?.nextStates ?? [];
  return nextStates.find(
    (state) => state.stateName?.toLowerCase() === target
  )?.stateId;
};

/**
 * Minutes between `startedAt` and now, clamped the way PostAppointment clamps it: an
 * appointment left open is not allowed to report more than an hour over its booked
 * length.
 */
export const elapsedMinutesCapped = (
  startedAt: string | null,
  scheduledMinutes?: number | null
): number => {
  if (!startedAt) {
    return 0;
  }

  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) {
    return 0;
  }

  const minutes = Math.max(0, Math.round((Date.now() - started) / 60000));
  const cap =
    typeof scheduledMinutes === "number" ? scheduledMinutes + 60 : undefined;

  return cap !== undefined && minutes > cap ? cap : minutes;
};
